/**
 * useDataAssistantTools
 * Builds tool definitions for the dashboard data assistant.
 * Extracted here to keep the UI component below 500 lines.
 */

import { useCallback } from "react"
import { tool } from "ai"
import { z } from "zod"
import { format, formatDistanceToNow } from "date-fns"
import { generateText } from "ai"
import { createModel } from "@/lib/agents/utils"
import { app } from "@/lib/app"
import { generateTelerivetReport, downloadPDF, type ReportData } from "@/pages/dev/dashboard/utils/generateTelerivetReport"
import type {
  RawContact, RawMessage, TelerivetProject,
  OverviewAnalytics, DailyTrend, FlagBreakdown
} from "@/pages/dev/dashboard/components/types"
import { computeVarBreakdown, formatVarValue, type PinnedVarConfig } from "@/pages/dev/dashboard/hooks/useVariablesAnalytics"

export interface AssistantCommentItem {
  contactId: string
  contact?: { id: string; name?: string; phone_number?: string }
  messageId?: string
  commentText: string
  commenterName: string
  timestamp: number
  type: 'message' | 'conversation'
}

export interface AssistantCorrectedResponseItem {
  messageId: string
  contactId: string
  content: string
  correctedResponse: string
  timestamp: number
  contact?: { id: string; name?: string; phone_number?: string }
}

interface UseDataAssistantToolsOptions {
  contacts: RawContact[]
  messages: RawMessage[]
  contactMap: Map<string, RawContact>
  commentsList?: AssistantCommentItem[]
  correctedResponsesList?: AssistantCorrectedResponseItem[]
  overviewAnalytics: OverviewAnalytics
  dailyTrends: DailyTrend[]
  flagBreakdown: FlagBreakdown[]
  dateRangeLabel: string
  projects: TelerivetProject[]
  allProjects: TelerivetProject[]
  teamId: string | undefined
  allVarKeys?: { contact: string[]; message: string[]; form?: string[] }
  pinnedVarKeys?: string[]
  pinnedVarConfigs?: Record<string, PinnedVarConfig>
  onTogglePin?: (key: string) => void
  onUpdatePinnedConfig?: (key: string, updates: Partial<PinnedVarConfig>) => void
  /** Field titles + option labels for form.* vars, keyed by "form.<field>". */
  formFieldMeta?: Record<string, { title: string; type?: string; optionLabels?: Record<string, string>; formId?: string; formTitle?: string }>
}

export function useDataAssistantTools({
  contacts, messages, contactMap,
  commentsList = [], correctedResponsesList = [],
  overviewAnalytics, dailyTrends, flagBreakdown,
  dateRangeLabel, projects, allProjects, teamId,
  allVarKeys, pinnedVarKeys = [], pinnedVarConfigs = {},
  onTogglePin, onUpdatePinnedConfig, formFieldMeta = {},
}: UseDataAssistantToolsOptions) {
  const createTools = useCallback(() => {
    const formKeys = new Set(allVarKeys?.form || [])
    // Human label for a variable key; form fields resolve to their form title.
    const varTitle = (key: string): string => {
      const meta = formFieldMeta[key]
      if (meta?.title) return meta.title
      return key.startsWith("form.") ? key.slice("form.".length) : key
    }
    // Human label for a stored option value on a list/multiselect form field.
    const varValueLabel = (key: string, value: string): string => {
      const labels = formFieldMeta[key]?.optionLabels
      return (labels && labels[value]) || value
    }

    // Every variable key the dashboard actually knows about — the ONLY keys that
    // are valid to pin. Includes form fields defined in team forms (even unpopulated)
    // plus contact/message vars observed on the data. Guards against the model
    // inventing plausible-sounding-but-nonexistent names (e.g. "quality_assessment_metrics").
    // Form keys are limited to fields defined on a real team form (Object.keys
    // of formFieldMeta) — orphan form values from deleted/test forms are excluded
    // so they can't be listed or pinned.
    const knownVarKeys = new Set<string>([
      ...(allVarKeys?.contact || []),
      ...(allVarKeys?.message || []),
      ...Object.keys(formFieldMeta),
    ])
    // Suggest real keys when the model passes an unknown one — matched by raw key
    // or human label (so a guess like "Household Size" resolves to "form.household_size").
    const suggestKeys = (name: string, max = 8): Array<{ variable: string; label: string }> => {
      const needle = name.toLowerCase().replace(/[\s_.-]+/g, "")
      const scored: Array<{ variable: string; label: string; score: number }> = []
      for (const key of knownVarKeys) {
        const label = varTitle(key)
        const hayKey = key.toLowerCase().replace(/[\s_.-]+/g, "")
        const hayLabel = label.toLowerCase().replace(/[\s_.-]+/g, "")
        let score = 0
        if (hayKey === needle || hayLabel === needle) score = 3
        else if (hayKey.includes(needle) || hayLabel.includes(needle) || needle.includes(hayLabel)) score = 2
        else if (needle && (hayKey.startsWith(needle.slice(0, 4)) || hayLabel.startsWith(needle.slice(0, 4)))) score = 1
        if (score > 0) scored.push({ variable: key, label, score })
      }
      return scored.sort((a, b) => b.score - a.score).slice(0, max).map(({ variable, label }) => ({ variable, label }))
    }

    return {
      search_messages: tool({
        description: "Search through messages by keyword, contact name/phone, direction, or project.",
        parameters: z.object({
          query: z.string().nullable(),
          contact_name: z.string().nullable(),
          contact_phone: z.string().nullable(),
          direction: z.enum(["incoming", "outgoing", "all"]).nullable(),
          project_name: z.string().nullable(),
          limit: z.number().nullable().transform(v => v ?? 20),
        }),
        execute: async ({ query, contact_name, contact_phone, direction, project_name, limit = 20 }) => {
          let filtered = [...messages]
          if (project_name) filtered = filtered.filter(m => m._projectName?.toLowerCase().includes(project_name.toLowerCase()))
          if (query) filtered = filtered.filter(m => m.content?.toLowerCase().includes(query.toLowerCase()))
          if (contact_name) filtered = filtered.filter(m => contactMap.get(m.contact_id)?.name?.toLowerCase().includes(contact_name.toLowerCase()))
          if (contact_phone) filtered = filtered.filter(m => contactMap.get(m.contact_id)?.phone_number?.includes(contact_phone))
          if (direction && direction !== "all") filtered = filtered.filter(m => m.direction === direction)
          filtered.sort((a, b) => b.time_created - a.time_created)
          const byProject: Record<string, number> = {}
          filtered.forEach(m => { const p = m._projectName || "Unknown"; byProject[p] = (byProject[p] || 0) + 1 })
          return {
            total_matches: filtered.length,
            showing: Math.min(filtered.length, limit),
            by_project: Object.entries(byProject).map(([name, count]) => ({ project: name, count })),
            messages: filtered.slice(0, limit).map(m => {
              const c = contactMap.get(m.contact_id)
              return { project: m._projectName || "Unknown", contact_name: c?.name || "Unknown", contact_phone: c?.phone_number || "N/A", direction: m.direction, content: m.content?.substring(0, 500), time: format(new Date(m.time_created * 1000), "MMM d, yyyy h:mm a"), contact_id: m.contact_id }
            })
          }
        }
      }),

      get_recent_messages: tool({
        description: "Get the most recent messages, optionally filtered by contact.",
        parameters: z.object({ contact_id: z.string().nullable(), limit: z.number().nullable().transform(v => v ?? 15) }),
        execute: async ({ contact_id, limit = 15 }) => {
          let filtered = contact_id ? messages.filter(m => m.contact_id === contact_id) : [...messages]
          filtered.sort((a, b) => b.time_created - a.time_created)
          return filtered.slice(0, limit).map(m => {
            const c = contactMap.get(m.contact_id)
            return { contact_name: c?.name || "Unknown", contact_phone: c?.phone_number, direction: m.direction, content: m.content?.substring(0, 300), time: format(new Date(m.time_created * 1000), "MMM d, h:mm a"), time_ago: formatDistanceToNow(new Date(m.time_created * 1000), { addSuffix: true }) }
          })
        }
      }),

      get_contact_conversation: tool({
        description: "Get the full conversation history for a specific contact.",
        parameters: z.object({ contact_name: z.string().nullable(), contact_phone: z.string().nullable(), contact_id: z.string().nullable(), limit: z.number().nullable().transform(v => v ?? 30) }),
        execute: async ({ contact_name, contact_phone, contact_id, limit = 30 }) => {
          let target = contact_id ? contactMap.get(contact_id) : contact_phone ? contacts.find(c => c.phone_number?.includes(contact_phone)) : contacts.find(c => c.name?.toLowerCase().includes(contact_name?.toLowerCase() || ""))
          if (!target) return { error: "Contact not found", searched_for: { contact_name, contact_phone, contact_id } }
          const msgs = messages.filter(m => m.contact_id === target!.id).sort((a, b) => a.time_created - b.time_created).slice(-limit)
          return { contact: { id: target.id, name: target.name || "Unknown", phone: target.phone_number, created: format(new Date(target.time_created * 1000), "MMM d, yyyy"), message_count: target.message_count, custom_vars: target.vars }, conversation: msgs.map(m => ({ direction: m.direction, content: m.content, time: format(new Date(m.time_created * 1000), "MMM d, h:mm a") })), total_messages: msgs.length }
        }
      }),

      get_contact_details: tool({
        description: "Get detailed information about a contact including all custom variables.",
        parameters: z.object({ contact_name: z.string().nullable(), contact_phone: z.string().nullable(), contact_id: z.string().nullable() }),
        execute: async ({ contact_name, contact_phone, contact_id }) => {
          let target = contact_id ? contactMap.get(contact_id) : contact_phone ? contacts.find(c => c.phone_number?.includes(contact_phone)) : contacts.find(c => c.name?.toLowerCase().includes(contact_name?.toLowerCase() || ""))
          if (!target) return { error: "Contact not found" }
          const cMsgs = messages.filter(m => m.contact_id === target!.id)
          return { id: target.id, name: target.name, phone_number: target.phone_number, created: format(new Date(target.time_created * 1000), "MMM d, yyyy"), last_message: target.last_message_time ? formatDistanceToNow(new Date(target.last_message_time * 1000), { addSuffix: true }) : "N/A", total_messages: cMsgs.length, incoming_messages: cMsgs.filter(m => m.direction === "incoming").length, outgoing_messages: cMsgs.filter(m => m.direction === "outgoing").length, custom_variables: target.vars || {} }
        }
      }),

      search_extractions: tool({
        description: "Search contact/message extraction payloads by schema id/key/value.",
        parameters: z.object({
          schema_id: z.string().nullable(),
          key: z.string().nullable(),
          query: z.string().nullable(),
          source: z.enum(["contacts", "messages", "both"]).nullable().transform(v => v ?? "both"),
          limit: z.number().nullable().transform(v => v ?? 25),
        }),
        execute: async ({ schema_id, key, query, source = "both", limit = 25 }) => {
          const needle = (query || "").toLowerCase()
          const rows: Array<{ source: "contact" | "message"; id: string; contact_id?: string; match: any }> = []
          const includeSource = (s: "contacts" | "messages") => source === "both" || source === s

          if (includeSource("contacts")) {
            contacts.forEach((c) => {
              const vars = c.vars || {}
              const extractionRoot = vars.extractions || vars.analysis_extractions || {}
              const entries = schema_id ? { [schema_id]: extractionRoot[schema_id] } : extractionRoot
              Object.entries(entries || {}).forEach(([sid, payload]: [string, any]) => {
                const data = payload?.extracted_data || payload || {}
                Object.entries(data).forEach(([k, v]) => {
                  if (key && k !== key) return
                  const valueText = String(v ?? "").toLowerCase()
                  if (needle && !valueText.includes(needle)) return
                  rows.push({ source: "contact", id: c.id, match: { schema_id: sid, key: k, value: v } })
                })
              })
            })
          }

          if (includeSource("messages")) {
            messages.forEach((m) => {
              const vars = m.vars || {}
              const extractionRoot = vars.extractions || {}
              const entries = schema_id ? { [schema_id]: extractionRoot[schema_id] } : extractionRoot
              Object.entries(entries || {}).forEach(([sid, payload]: [string, any]) => {
                const data = payload?.extracted_data || payload || {}
                Object.entries(data).forEach(([k, v]) => {
                  if (key && k !== key) return
                  const valueText = String(v ?? "").toLowerCase()
                  if (needle && !valueText.includes(needle)) return
                  rows.push({ source: "message", id: m.id, contact_id: m.contact_id, match: { schema_id: sid, key: k, value: v } })
                })
              })
            })
          }

          return {
            total: rows.length,
            showing: Math.min(limit, rows.length),
            items: rows.slice(0, limit),
          }
        },
      }),

      get_analytics_summary: tool({
        description: "Get a comprehensive summary of analytics for the current date range.",
        parameters: z.object({ include_trends: z.boolean().nullable().transform(v => v ?? true), include_flags: z.boolean().nullable().transform(v => v ?? true) }),
        execute: async ({ include_trends = true, include_flags = true }) => {
          const result: any = { date_range: dateRangeLabel, users: { total: overviewAnalytics.totalContacts, new: overviewAnalytics.newUsers, active: overviewAnalytics.activeUsers, human_handled: overviewAnalytics.humanHandled, bot_handled: overviewAnalytics.botHandled, human_takeovers: overviewAnalytics.humanTakeovers }, messages: { total: overviewAnalytics.totalMessages, incoming: overviewAnalytics.incomingMessages, outgoing: overviewAnalytics.outgoingMessages, avg_per_user: overviewAnalytics.avgMessagesPerUser, response_rate: overviewAnalytics.responseRate + "%", median_response_time: formatDuration(overviewAnalytics.medianResponseTime) }, feedback: { thumbs_up: overviewAnalytics.thumbsUp, thumbs_down: overviewAnalytics.thumbsDown, positive_rate: overviewAnalytics.thumbsUpRate + "%", total_rated: overviewAnalytics.ratedCount, corrections: overviewAnalytics.correctedResponses } }
          if (include_trends && dailyTrends.length > 0) result.daily_trends = dailyTrends.slice(-7).map(d => ({ date: d.date, messages: d.incoming + d.outgoing, new_users: d.newUsers, active_users: d.activeUsers }))
          if (include_flags && flagBreakdown.length > 0) result.flags = flagBreakdown.map(f => ({ name: f.name, active: f.active, resolved: f.resolved, total: f.active + f.resolved }))
          return result
        }
      }),

      find_contacts: tool({
        description: "Search for contacts by name, phone, or custom variable values.",
        parameters: z.object({ name: z.string().nullable(), phone: z.string().nullable(), var_name: z.string().nullable(), var_value: z.string().nullable(), limit: z.number().nullable().transform(v => v ?? 20) }),
        execute: async ({ name, phone, var_name, var_value, limit = 20 }) => {
          let filtered = [...contacts]
          if (name) filtered = filtered.filter(c => c.name?.toLowerCase().includes(name.toLowerCase()))
          if (phone) filtered = filtered.filter(c => c.phone_number?.includes(phone))
          if (var_name && var_value) filtered = filtered.filter(c => { const v = c.vars?.[var_name]; return v && String(v).toLowerCase().includes(var_value.toLowerCase()) })
          return { total_matches: filtered.length, showing: Math.min(filtered.length, limit), contacts: filtered.slice(0, limit).map(c => ({ id: c.id, name: c.name || "Unknown", phone: c.phone_number, created: format(new Date(c.time_created * 1000), "MMM d, yyyy"), message_count: c.message_count, vars: c.vars })) }
        }
      }),

      get_escalated_conversations: tool({
        description: "Get detailed information about escalated and flagged conversations.",
        parameters: z.object({ flag_type: z.enum(["all", "high_risk", "low_confidence", "asked_human", "escalated"]).nullable().transform(v => v ?? "all"), risk_level: z.string().nullable(), risk_category: z.string().nullable(), only_unresolved: z.boolean().nullable().transform(v => v ?? true), limit: z.number().nullable().transform(v => v ?? 20) }),
        execute: async ({ flag_type = "all", risk_level, risk_category, only_unresolved = true, limit = 20 }) => {
          const checkFlag = (vars: any, key: string) => only_unresolved ? vars[key] === "flagged" : (vars[key] === "flagged" || vars[key] === "resolved")
          let flagged = messages.filter(m => {
            if (m.direction !== "incoming") return false
            const v = m.vars || {}
            if (flag_type === "high_risk") return checkFlag(v, "high_risk_flag")
            if (flag_type === "low_confidence") return checkFlag(v, "low_confidence_flag")
            if (flag_type === "asked_human") return checkFlag(v, "asked_human_flag")
            if (flag_type === "escalated") return v.escalation === "true"
            return checkFlag(v, "high_risk_flag") || checkFlag(v, "low_confidence_flag") || checkFlag(v, "asked_human_flag") || v.escalation === "true"
          })
          if (risk_level) flagged = flagged.filter(m => (m.vars?.risk_category_level || "").toLowerCase().includes(risk_level.toLowerCase()))
          if (risk_category) flagged = flagged.filter(m => (m.vars?.risk_category_name || "").toLowerCase().includes(risk_category.toLowerCase()))
          flagged.sort((a, b) => b.time_created - a.time_created)
          const categories: Record<string, number> = {}; const levels: Record<string, number> = {}
          flagged.forEach(m => { const cat = m.vars?.risk_category_name; const lvl = m.vars?.risk_category_level; if (cat) categories[String(cat)] = (categories[String(cat)] || 0) + 1; if (lvl) levels[String(lvl)] = (levels[String(lvl)] || 0) + 1 })
          return { summary: { total_flagged: flagged.length, high_risk: flagged.filter(m => m.vars?.high_risk_flag === "flagged").length, low_confidence: flagged.filter(m => m.vars?.low_confidence_flag === "flagged").length, asked_human: flagged.filter(m => m.vars?.asked_human_flag === "flagged").length, escalated: flagged.filter(m => m.vars?.escalation === "true").length }, category_breakdown: Object.entries(categories).sort((a, b) => b[1] - a[1]).map(([name, count]) => ({ name, count })), level_breakdown: Object.entries(levels).sort((a, b) => b[1] - a[1]).map(([level, count]) => ({ level, count })), showing: Math.min(flagged.length, limit), conversations: flagged.slice(0, limit).map(m => { const c = contactMap.get(m.contact_id); const v = m.vars || {}; return { contact_name: c?.name || "Unknown", contact_phone: c?.phone_number || "N/A", contact_id: m.contact_id, message_content: m.content?.substring(0, 300), time: format(new Date(m.time_created * 1000), "MMM d, yyyy h:mm a"), flags: { high_risk: v.high_risk_flag || null, low_confidence: v.low_confidence_flag || null, asked_human: v.asked_human_flag || null, escalation: v.escalation || null }, risk_category: v.risk_category_name || null, risk_level: v.risk_category_level || null, ai_explanation: v.ai_explanation || null, handled_by: c?.vars?.handled_by || "bot", project: m._projectName || "Unknown" } }) }
        }
      }),

      get_variables_breakdown: tool({
        description: "Analyze the distribution of values for any variable — contact, message, or form field (key like \"form.<field>\"). Form fields resolve to their human title and map option values to their labels.",
        parameters: z.object({ variable_name: z.string(), source: z.enum(["contacts", "messages", "both"]).nullable().transform(v => v ?? "contacts"), top_n: z.number().nullable().transform(v => v ?? 15) }),
        execute: async ({ variable_name, source = "contacts", top_n = 15 }) => {
          const isForm = variable_name.startsWith("form.") || !!formFieldMeta[variable_name] || formKeys.has(variable_name)
          const valueCounts: Record<string, number> = {}
          const countFrom = (items: any[]) => items.forEach(item => { const val = item.vars?.[variable_name]; if (val !== undefined && val !== null && val !== "") { const str = formatVarValue(val); valueCounts[str] = (valueCounts[str] || 0) + 1 } })
          // Form vars only live on contacts; default there even if the caller said messages.
          if (source === "contacts" || source === "both" || isForm) countFrom(contacts)
          if (source === "messages" || source === "both") countFrom(messages)
          if (Object.keys(valueCounts).length === 0) {
            // Distinguish "not a real variable" from "real but empty in this range".
            if (!knownVarKeys.has(variable_name)) {
              return { variable: variable_name, error: `"${variable_name}" is not a known variable.`, hint: "Call list_variables for exact keys; don't guess.", did_you_mean: suggestKeys(variable_name) }
            }
            return { variable: variable_name, label: varTitle(variable_name), is_form_field: isForm, source, total_with_value: 0, error: `"${variable_name}" exists but has no values in the current date range.` }
          }
          const total = Object.values(valueCounts).reduce((a, b) => a + b, 0)
          const base = source === "messages" ? messages.length : contacts.length
          const sorted = Object.entries(valueCounts).sort((a, b) => b[1] - a[1]).slice(0, top_n)
          return { variable: variable_name, label: varTitle(variable_name), is_form_field: isForm, source, total_with_value: total, total_records: base, coverage: `${((total / base) * 100).toFixed(1)}%`, distinct_values: Object.keys(valueCounts).length, showing: sorted.length, breakdown: sorted.map(([value, count]) => ({ value, label: varValueLabel(variable_name, value), count, percentage: `${((count / total) * 100).toFixed(1)}%` })) }
        }
      }),

      list_forms: tool({
        description: "List the team's forms and the fields on each one. Use this FIRST whenever the user refers to variables 'from a form' or names a form (e.g. 'pin the fields from the Quality Assessment form'). Returns each form's title plus its fields with the exact `variable` key to pass to pin_variable. Field keys look like \"form.<field>\". Match the user's form name to a `form_title` here, then pin that form's field keys — never guess field names.",
        parameters: z.object({
          form_name: z.string().nullable().describe("Optional filter: only return forms whose title contains this text (case-insensitive)."),
          include_empty: z.boolean().nullable().transform(v => v ?? true).describe("Include fields with no captured values in the current date range."),
        }),
        execute: async ({ form_name, include_empty = true }) => {
          // Group form fields by their owning form. Only fields defined on a real
          // form are included — orphan values (from deleted/test forms) are skipped.
          const byForm = new Map<string, { formId?: string; formTitle: string; fields: Array<{ variable: string; label: string; type?: string; populated: boolean; records_with_value: number; distinct_values: number }> }>()

          const addField = (key: string) => {
            const meta = formFieldMeta[key]
            if (!meta) return // skip orphans — not on any known form
            const groupId = meta.formId ?? "__nogroup__"
            const formTitle = meta.formTitle ?? "(untitled form)"
            if (!byForm.has(groupId)) byForm.set(groupId, { formId: meta.formId, formTitle, fields: [] })
            const breakdown = computeVarBreakdown(key, contacts as any, messages as any)
            const total = breakdown.reduce((s, b) => s + b.count, 0)
            byForm.get(groupId)!.fields.push({
              variable: key,
              label: varTitle(key),
              type: meta.type,
              populated: total > 0,
              records_with_value: total,
              distinct_values: breakdown.length,
            })
          }

          for (const key of Object.keys(formFieldMeta)) addField(key)

          let forms = Array.from(byForm.values()).map(f => ({
            ...f,
            fields: (include_empty ? f.fields : f.fields.filter(x => x.populated))
              .sort((a, b) => a.label.localeCompare(b.label)),
          }))
          if (form_name) {
            const needle = form_name.toLowerCase()
            forms = forms.filter(f => f.formTitle.toLowerCase().includes(needle))
          }
          forms = forms
            .filter(f => f.fields.length > 0)
            .sort((a, b) => a.formTitle.localeCompare(b.formTitle))

          return {
            total_forms: forms.length,
            ...(form_name && forms.length === 0 ? { note: `No form matched "${form_name}". Call list_forms with no filter to see all form titles.` } : {}),
            guidance: "To pin variables 'from a form', take that form's field `variable` keys here and call pin_variable for each. Prefer populated=true fields.",
            forms,
          }
        }
      }),

      list_variables: tool({
        description: "List the available variables you can inspect or pin — contact vars, message vars, and form fields — each with its exact key, human label, source form (for form fields), how populated it is in the current date range, distinct value count, and whether it's pinned. This is the ONLY source of valid variable keys; always call it before pin_variable and pass back an exact `variable` key. Form fields appear even when no contact has filled them yet (populated=false). To work with the fields of a SPECIFIC named form, use list_forms instead.",
        parameters: z.object({
          source: z.enum(["contacts", "messages", "form", "both", "all"]).nullable().transform(v => v ?? "all"),
          include_sample_values: z.boolean().nullable().transform(v => v ?? true),
          // By default hides form fields nobody has filled in range; set true to see them all.
          include_empty: z.boolean().nullable().transform(v => v ?? false),
        }),
        execute: async ({ source = "all", include_sample_values = true, include_empty = false }) => {
          const wantContacts = source === "contacts" || source === "both" || source === "all"
          const wantMessages = source === "messages" || source === "both" || source === "all"
          const wantForm = source === "form" || source === "all"

          const contactKeys = new Set(allVarKeys?.contact || [])
          const messageKeys = new Set(allVarKeys?.message || [])
          // Form keys = only fields defined on a real team form. Orphan form values
          // (from deleted/test forms) are intentionally excluded.
          const formKeyUnion = new Set<string>(Object.keys(formFieldMeta))

          const kindOf = (key: string): "contact" | "message" | "form" => {
            if (formKeyUnion.has(key) || key.startsWith("form.")) return "form"
            if (messageKeys.has(key)) return "message"
            return "contact"
          }

          const keys = new Set<string>()
          if (wantContacts) contactKeys.forEach(k => keys.add(k))
          if (wantMessages) messageKeys.forEach(k => keys.add(k))
          if (wantForm) formKeyUnion.forEach(k => keys.add(k))
          // Fallback: nothing enumerated (no metadata yet) — scrape from raw data.
          if (keys.size === 0) {
            contacts.forEach(c => Object.keys(c.vars || {}).forEach(k => keys.add(k)))
            messages.forEach(m => Object.keys((m as any).vars || {}).forEach(k => keys.add(k)))
          }

          const base = contacts.length || 1
          let rows = Array.from(keys).map(key => {
            const breakdown = computeVarBreakdown(key, contacts as any, messages as any)
            const total = breakdown.reduce((sum, b) => sum + b.count, 0)
            const kind = kindOf(key)
            const meta = formFieldMeta[key]
            return {
              variable: key,
              label: varTitle(key),
              kind,
              ...(kind === "form" ? { form: meta?.formTitle ?? "(unknown form)", field_type: meta?.type ?? null } : {}),
              populated: total > 0,
              pinned: pinnedVarKeys.includes(key),
              pinned_config: pinnedVarConfigs[key] ?? null,
              records_with_value: total,
              coverage: `${Math.min(100, (total / base) * 100).toFixed(1)}%`,
              distinct_values: breakdown.length,
              ...(include_sample_values ? { top_values: breakdown.slice(0, 5).map(b => ({ value: b.value, label: varValueLabel(key, b.value), count: b.count })) } : {}),
            }
          })

          const hiddenEmpty = !include_empty ? rows.filter(r => !r.populated).length : 0
          if (!include_empty) rows = rows.filter(r => r.populated)
          // Populated first, then by coverage desc, then alphabetically by label.
          rows.sort((a, b) =>
            (Number(b.populated) - Number(a.populated)) ||
            (b.records_with_value - a.records_with_value) ||
            a.label.localeCompare(b.label)
          )

          return {
            total_variables: rows.length,
            hidden_empty_form_fields: hiddenEmpty,
            currently_pinned: pinnedVarKeys,
            pinning_available: !!onTogglePin,
            guidance: "Only pin variables listed here, using the exact `variable` key. Prefer populated=true fields (populated=false means no data in the current date range, so the pinned card would read 'No data'). Good pins have moderate cardinality (2–12 distinct values) and decent coverage. chart_type: 'number' for single/near-constant values, 'pie' for ≤6 categories, 'bar' otherwise. Avoid free-text or near-unique variables. Refer to a variable by its `label` when talking to the user; pass the raw `variable` key to pin/unpin tools. Set include_empty=true to also see unfilled form fields.",
            variables: rows,
          }
        }
      }),

      pin_variable: tool({
        description: "Pin an EXISTING variable to the dashboard Overview, optionally configuring how it renders. You MUST call list_variables first and pass one of the exact `variable` keys it returned — never invent or guess a variable name. Form fields use keys like \"form.<field>\". Passing an unknown name is rejected.",
        parameters: z.object({
          variable_name: z.string().describe("Exact variable key from list_variables (e.g. \"form.household_size\", not a made-up name)."),
          chart_type: z.enum(["bar", "pie", "number"]).nullable(),
          sort_order: z.enum(["count_desc", "count_asc", "alpha_asc", "alpha_desc"]).nullable(),
          hide_null: z.boolean().nullable(),
          reason: z.string().nullable().describe("Why this variable is worth pinning"),
        }),
        execute: async ({ variable_name, chart_type, sort_order, hide_null, reason }) => {
          if (!onTogglePin) return { error: "Pinning is not available in this context." }
          // Reject invented/unknown keys so we never pin an empty "No data" card.
          if (!knownVarKeys.has(variable_name)) {
            return {
              error: `"${variable_name}" is not a real variable, so it was not pinned.`,
              hint: "Call list_variables and pin one of the exact `variable` keys it returns. Do not guess names.",
              did_you_mean: suggestKeys(variable_name),
            }
          }
          const already = pinnedVarKeys.includes(variable_name)
          if (!already) onTogglePin(variable_name)
          const updates: Partial<PinnedVarConfig> = {}
          if (chart_type) updates.chartType = chart_type
          if (sort_order) updates.sortOrder = sort_order
          if (hide_null != null) updates.hideNull = hide_null
          if (Object.keys(updates).length && onUpdatePinnedConfig) onUpdatePinnedConfig(variable_name, updates)
          return { pinned: variable_name, label: varTitle(variable_name), was_already_pinned: already, applied_config: updates, reason: reason || null }
        }
      }),

      pin_variables: tool({
        description: "Pin SEVERAL existing variables to the Overview at once — use this when the user asks to pin multiple variables or 'the fields from a form'. Get the exact keys from list_forms (for a named form) or list_variables first. Unknown keys are skipped and reported, never invented.",
        parameters: z.object({
          variable_names: z.array(z.string()).min(1).describe("Exact variable keys (e.g. [\"form.household_size\", \"form.monthly_gross_income\"])."),
          chart_type: z.enum(["bar", "pie", "number"]).nullable().describe("Applied to every variable pinned in this call."),
          sort_order: z.enum(["count_desc", "count_asc", "alpha_asc", "alpha_desc"]).nullable(),
          hide_null: z.boolean().nullable(),
          reason: z.string().nullable(),
        }),
        execute: async ({ variable_names, chart_type, sort_order, hide_null, reason }) => {
          if (!onTogglePin) return { error: "Pinning is not available in this context." }
          const pinned: string[] = []
          const skipped: Array<{ variable: string; why: string }> = []
          const alreadyPinned: string[] = []
          const updates: Partial<PinnedVarConfig> = {}
          if (chart_type) updates.chartType = chart_type
          if (sort_order) updates.sortOrder = sort_order
          if (hide_null != null) updates.hideNull = hide_null

          // De-dupe input so we never toggle the same key twice in one call.
          for (const name of Array.from(new Set(variable_names))) {
            if (!knownVarKeys.has(name)) {
              skipped.push({ variable: name, why: "not a real variable" })
              continue
            }
            if (pinnedVarKeys.includes(name)) {
              alreadyPinned.push(name)
            } else {
              onTogglePin(name)
              pinned.push(name)
            }
            if (Object.keys(updates).length && onUpdatePinnedConfig) onUpdatePinnedConfig(name, updates)
          }
          return {
            pinned: pinned.map(k => ({ variable: k, label: varTitle(k) })),
            already_pinned: alreadyPinned,
            skipped,
            applied_config: updates,
            reason: reason || null,
            ...(skipped.length ? { hint: "Skipped keys aren't real variables — get exact keys from list_forms / list_variables." } : {}),
          }
        }
      }),

      unpin_variable: tool({
        description: "Remove a variable from the dashboard Overview.",
        parameters: z.object({ variable_name: z.string(), reason: z.string().nullable() }),
        execute: async ({ variable_name, reason }) => {
          if (!onTogglePin) return { error: "Pinning is not available in this context." }
          if (!pinnedVarKeys.includes(variable_name)) return { error: `"${variable_name}" is not currently pinned.`, currently_pinned: pinnedVarKeys }
          onTogglePin(variable_name)
          return { unpinned: variable_name, reason: reason || null }
        }
      }),

      configure_pinned_variable: tool({
        description: "Change how an already-pinned variable renders on the Overview (chart type, sort order, whether empty values are hidden).",
        parameters: z.object({
          variable_name: z.string(),
          chart_type: z.enum(["bar", "pie", "number"]).nullable(),
          sort_order: z.enum(["count_desc", "count_asc", "alpha_asc", "alpha_desc"]).nullable(),
          hide_null: z.boolean().nullable(),
        }),
        execute: async ({ variable_name, chart_type, sort_order, hide_null }) => {
          if (!onUpdatePinnedConfig) return { error: "Pin configuration is not available in this context." }
          if (!pinnedVarKeys.includes(variable_name)) return { error: `"${variable_name}" is not pinned. Pin it first.`, currently_pinned: pinnedVarKeys }
          const updates: Partial<PinnedVarConfig> = {}
          if (chart_type) updates.chartType = chart_type
          if (sort_order) updates.sortOrder = sort_order
          if (hide_null != null) updates.hideNull = hide_null
          if (!Object.keys(updates).length) return { error: "No configuration changes supplied." }
          onUpdatePinnedConfig(variable_name, updates)
          return { variable: variable_name, applied_config: updates }
        }
      }),

      get_top_contacts: tool({
        description: "Get the most active contacts ranked by message count or activity.",
        parameters: z.object({ sort_by: z.enum(["message_count", "most_recent", "oldest_contact", "newest_contact"]).nullable().transform(v => v ?? "message_count"), limit: z.number().nullable().transform(v => v ?? 20), project_name: z.string().nullable() }),
        execute: async ({ sort_by = "message_count", limit = 20, project_name }) => {
          let filtered = project_name ? contacts.filter(c => c._projectName?.toLowerCase().includes(project_name.toLowerCase())) : [...contacts]
          if (sort_by === "message_count") filtered.sort((a, b) => (b.message_count || 0) - (a.message_count || 0))
          else if (sort_by === "most_recent") filtered.sort((a, b) => (b.last_message_time || 0) - (a.last_message_time || 0))
          else if (sort_by === "oldest_contact") filtered.sort((a, b) => a.time_created - b.time_created)
          else filtered.sort((a, b) => b.time_created - a.time_created)
          return { total_contacts: filtered.length, sorted_by: sort_by, showing: Math.min(filtered.length, limit), contacts: filtered.slice(0, limit).map(c => ({ id: c.id, name: c.name || "Unknown", phone: c.phone_number, project: c._projectName || "Unknown", message_count: c.message_count || 0, last_message: c.last_message_time ? formatDistanceToNow(new Date(c.last_message_time * 1000), { addSuffix: true }) : "Never", joined: format(new Date(c.time_created * 1000), "MMM d, yyyy"), vars: c.vars })) }
        }
      }),

      get_activity_patterns: tool({
        description: "Analyze when users are most active — hourly, day-of-week, or daily patterns.",
        parameters: z.object({ granularity: z.enum(["hourly", "by_day_of_week", "daily"]).nullable().transform(v => v ?? "hourly") }),
        execute: async ({ granularity = "hourly" }) => {
          const incoming = messages.filter(m => m.direction === "incoming")
          if (granularity === "hourly") {
            const hours = new Array(24).fill(0)
            incoming.forEach(m => { hours[new Date(m.time_created * 1000).getHours()]++ })
            const peak = hours.indexOf(Math.max(...hours))
            return { type: "hourly_distribution", peak_hour: `${peak.toString().padStart(2, "0")}:00–${(peak + 1).toString().padStart(2, "0")}:00`, peak_count: hours[peak], total_incoming: incoming.length, hours: hours.map((count, h) => ({ hour: `${h.toString().padStart(2, "0")}:00`, incoming_messages: count })) }
          }
          if (granularity === "by_day_of_week") {
            const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"]
            const dayCounts = new Array(7).fill(0)
            incoming.forEach(m => { dayCounts[new Date(m.time_created * 1000).getDay()]++ })
            const peak = dayCounts.indexOf(Math.max(...dayCounts))
            return { type: "day_of_week", busiest_day: days[peak], distribution: dayCounts.map((count, i) => ({ day: days[i], incoming_messages: count })) }
          }
          return { type: "daily", days: dailyTrends.map(d => ({ date: d.date, incoming: d.incoming, outgoing: d.outgoing, total: d.incoming + d.outgoing, new_users: d.newUsers, active_users: d.activeUsers })) }
        }
      }),

      get_ratings_analysis: tool({
        description: "Get detailed breakdown of user feedback ratings (thumbs up/down and corrected responses).",
        parameters: z.object({ sentiment: z.enum(["all", "positive", "negative", "corrected"]).nullable().transform(v => v ?? "all"), limit: z.number().nullable().transform(v => v ?? 20) }),
        execute: async ({ sentiment = "all", limit = 20 }) => {
          const thumbsUp = messages.filter(m => m.vars?.rating === "thumbs_up" || m.vars?.rating === "1" || m.vars?.rating === 1)
          const thumbsDown = messages.filter(m => m.vars?.rating === "thumbs_down" || m.vars?.rating === "0" || m.vars?.rating === 0)
          const corrected = messages.filter(m => m.vars?.rating_example)
          let pool = sentiment === "positive" ? thumbsUp : sentiment === "negative" ? thumbsDown : sentiment === "corrected" ? corrected : [...thumbsUp, ...thumbsDown]
          pool = pool.sort((a, b) => b.time_created - a.time_created)
          return { summary: { thumbs_up: thumbsUp.length, thumbs_down: thumbsDown.length, corrected_responses: corrected.length, total_rated: thumbsUp.length + thumbsDown.length, satisfaction_rate: thumbsUp.length + thumbsDown.length > 0 ? `${((thumbsUp.length / (thumbsUp.length + thumbsDown.length)) * 100).toFixed(1)}%` : "N/A" }, showing: Math.min(pool.length, limit), messages: pool.slice(0, limit).map(m => { const c = contactMap.get(m.contact_id); return { contact: c?.name || c?.phone_number || "Unknown", direction: m.direction, content: m.content?.substring(0, 200), rating: m.vars?.rating || "unrated", correction: m.vars?.rating_example || null, time: format(new Date(m.time_created * 1000), "MMM d, h:mm a") } }) }
        }
      }),

      get_reviewer_comments: tool({
        description: "List reviewer comments (message-level and conversation-level) from the Messages tab. Use to answer questions about what reviewers said.",
        parameters: z.object({
          type: z.enum(["all", "message", "conversation"]).nullable().transform(v => v ?? "all"),
          commenter: z.string().nullable().describe("Filter by commenter name"),
          contact_phone: z.string().nullable().describe("Filter by contact phone"),
          query: z.string().nullable().describe("Search comment text"),
          limit: z.number().nullable().transform(v => v ?? 25),
        }),
        execute: async ({ type = "all", commenter, contact_phone, query, limit = 25 }) => {
          let list = [...commentsList]
          if (type !== "all") list = list.filter(c => c.type === type)
          if (commenter) list = list.filter(c => c.commenterName?.toLowerCase().includes(commenter.toLowerCase()))
          if (contact_phone) list = list.filter(c => c.contact?.phone_number?.includes(contact_phone))
          if (query) list = list.filter(c => c.commentText?.toLowerCase().includes(query.toLowerCase()))
          list.sort((a, b) => b.timestamp - a.timestamp)
          return {
            total: list.length,
            showing: Math.min(list.length, limit),
            by_type: { message: commentsList.filter(c => c.type === "message").length, conversation: commentsList.filter(c => c.type === "conversation").length },
            comments: list.slice(0, limit).map(c => ({
              type: c.type,
              contact: c.contact?.name || c.contact?.phone_number || c.contactId,
              contact_phone: c.contact?.phone_number,
              commenter: c.commenterName,
              comment: c.commentText?.substring(0, 400),
              time: format(new Date(c.timestamp * 1000), "MMM d, yyyy h:mm a"),
              message_id: c.messageId || null,
            })),
          }
        }
      }),

      get_corrected_responses: tool({
        description: "List messages where users gave thumbs-down and suggested a correction (what the bot should have said). Same data as Corrected Responses on the Messages tab.",
        parameters: z.object({ limit: z.number().nullable().transform(v => v ?? 20), contact_phone: z.string().nullable() }),
        execute: async ({ limit = 20, contact_phone }) => {
          let list = [...correctedResponsesList]
          if (contact_phone) list = list.filter(r => r.contact?.phone_number?.includes(contact_phone))
          list.sort((a, b) => b.timestamp - a.timestamp)
          return {
            total: list.length,
            showing: Math.min(list.length, limit),
            items: list.slice(0, limit).map(r => ({
              contact: r.contact?.name || r.contact?.phone_number || r.contactId,
              contact_phone: r.contact?.phone_number,
              bot_said: r.content?.substring(0, 300),
              should_have_said: r.correctedResponse?.substring(0, 300),
              time: format(new Date(r.timestamp * 1000), "MMM d, yyyy h:mm a"),
            })),
          }
        }
      }),

      get_escalation_metrics: tool({
        description: "Get detailed SLA and escalation metrics.",
        parameters: z.object({}),
        execute: async () => {
          const flagged = messages.filter(m => { const v = m.vars || {}; return v.high_risk_flag === "flagged" || v.low_confidence_flag === "flagged" || v.asked_human_flag === "flagged" || v.escalation === "true" })
          const escalatedIds = new Set(flagged.map(m => m.contact_id))
          const categories: Record<string, number> = {}; const levels: Record<string, number> = {}
          flagged.forEach(m => { const cat = m.vars?.risk_category_name; const lvl = m.vars?.risk_category_level; if (cat) categories[String(cat)] = (categories[String(cat)] || 0) + 1; if (lvl) levels[String(lvl)] = (levels[String(lvl)] || 0) + 1 })
          const needsReply = [...escalatedIds].filter(cid => { const msgs = messages.filter(m => m.contact_id === cid).sort((a, b) => b.time_created - a.time_created); return msgs[0]?.direction === "incoming" })
          return { escalation_summary: { total_flagged_messages: flagged.length, unique_escalated_contacts: escalatedIds.size, escalation_rate: contacts.length > 0 ? `${((escalatedIds.size / contacts.length) * 100).toFixed(1)}%` : "0%", high_risk_flags: flagged.filter(m => m.vars?.high_risk_flag === "flagged").length, low_confidence_flags: flagged.filter(m => m.vars?.low_confidence_flag === "flagged").length, asked_human_flags: flagged.filter(m => m.vars?.asked_human_flag === "flagged").length, contacts_needing_reply: needsReply.length }, flag_breakdown: flagBreakdown.map(f => ({ name: f.name, active: f.active, resolved: f.resolved, resolution_rate: f.active + f.resolved > 0 ? `${((f.resolved / (f.active + f.resolved)) * 100).toFixed(0)}%` : "N/A" })), by_risk_category: Object.entries(categories).sort((a, b) => b[1] - a[1]).map(([name, count]) => ({ category: name, count })), by_risk_level: Object.entries(levels).sort((a, b) => b[1] - a[1]).map(([level, count]) => ({ level, count })), top_contacts_needing_attention: needsReply.slice(0, 10).map(cid => { const c = contactMap.get(cid); return { name: c?.name || "Unknown", phone: c?.phone_number, contact_id: cid } }) }
        }
      }),

      generate_pdf_report: tool({
        description: "Generate a comprehensive PDF report with AI-generated insights. Ask the user for date range before calling.",
        parameters: z.object({ date_from: z.string().describe("Start date YYYY-MM-DD"), date_to: z.string().describe("End date YYYY-MM-DD") }),
        execute: async ({ date_from, date_to }) => {
          try {
            const reportFrom = new Date(date_from + "T00:00:00"); const reportTo = new Date(date_to + "T23:59:59")
            if (isNaN(reportFrom.getTime()) || isNaN(reportTo.getTime())) return { success: false, error: "Invalid date format. Use YYYY-MM-DD." }
            if (reportFrom > reportTo) return { success: false, error: "Start date must be before end date." }
            const projectName = projects.length === 1 ? projects[0].name : `${projects.length} Projects (${projects.map(p => p.name).join(", ")})`
            const incomingMsgs = messages.filter(m => m.direction === "incoming" && m.content)
            const wordFreq: Record<string, number> = {}
            const stopWords = new Set(["the","a","an","is","are","was","were","be","been","have","has","do","does","will","would","could","should","may","might","to","of","in","for","on","with","at","by","from","as","i","me","my","we","you","he","she","it","they","this","that","and","but","or","not","so","just"])
            incomingMsgs.slice(0, 500).forEach(msg => msg.content.toLowerCase().split(/\s+/).forEach(w => { const c = w.replace(/[^a-z]/g, ""); if (c.length > 3 && !stopWords.has(c)) wordFreq[c] = (wordFreq[c] || 0) + 1 }))
            const topTopics = Object.entries(wordFreq).sort((a, b) => b[1] - a[1]).slice(0, 10).map(([word, count]) => `${word} (${count} mentions)`)
            const totalEscalated = flagBreakdown.reduce((s, f) => s + f.active + f.resolved, 0)
            const activeEscalations = flagBreakdown.reduce((s, f) => s + f.active, 0)
            let aiSummary = "", aiInsights: string[] = [], aiRecommendations: string[] = [], topicsSummary = ""
            try {
              const apiKeys = await app.fetchAPIkeys(teamId || "")
              if (apiKeys.openai) {
                const aiModel = createModel(apiKeys, "openai/gpt-4o-mini")
                const statsContext = `Project: ${projectName}\nReport period: ${format(reportFrom, "MMM d, yyyy")} - ${format(reportTo, "MMM d, yyyy")}\nKEY METRICS:\n- Total messages: ${overviewAnalytics.totalMessages}\n- Active users: ${overviewAnalytics.activeUsers} | New: ${overviewAnalytics.newUsers}\n- Bot: ${overviewAnalytics.botHandled} | Human: ${overviewAnalytics.humanHandled}\n- Human takeovers (conversations requiring HITL): ${overviewAnalytics.humanTakeovers}\n- Response rate: ${overviewAnalytics.responseRate}%\n- Satisfaction: ${overviewAnalytics.thumbsUpRate}%\nESCALATIONS:\n${flagBreakdown.map(f => `- ${f.name}: ${f.active} active, ${f.resolved} resolved`).join("\n")}\nTOP TOPICS: ${topTopics.slice(0, 6).join(", ")}`
                const { text: aiText } = await generateText({ model: aiModel, messages: [{ role: "user", content: `Write a professional analytics report based on:\n${statsContext}\n\nRespond with EXACTLY this JSON:\n{"executive_summary":"...","key_insights":["..."],"recommendations":["..."],"topics_summary":"..."}` }] })
                try { const p = JSON.parse(aiText.trim()); aiSummary = p.executive_summary || ""; aiInsights = Array.isArray(p.key_insights) ? p.key_insights : []; aiRecommendations = Array.isArray(p.recommendations) ? p.recommendations : []; topicsSummary = p.topics_summary || "" } catch { aiSummary = aiText.substring(0, 600) }
              }
            } catch {}
            if (!aiSummary) aiSummary = `During ${format(reportFrom, "MMM d")} to ${format(reportTo, "MMM d, yyyy")}, ${projectName} processed ${overviewAnalytics.totalMessages.toLocaleString()} messages from ${overviewAnalytics.activeUsers.toLocaleString()} active users. Satisfaction: ${overviewAnalytics.thumbsUpRate}%.`
            if (!topicsSummary && topTopics.length > 0) topicsSummary = `Top topics: ${topTopics.slice(0, 5).join(", ")}.`
            const reportData: ReportData = { projectName, dateRange: { from: reportFrom, to: reportTo }, stats: { totalContacts: overviewAnalytics.totalContacts, totalMessages: overviewAnalytics.totalMessages, incomingMessages: overviewAnalytics.incomingMessages, outgoingMessages: overviewAnalytics.outgoingMessages, aiResponses: overviewAnalytics.aiResponses, humanResponses: overviewAnalytics.humanResponses, activeUsers: overviewAnalytics.activeUsers, newUsers: overviewAnalytics.newUsers, humanHandled: overviewAnalytics.humanHandled, botHandled: overviewAnalytics.botHandled, humanTakeovers: overviewAnalytics.humanTakeovers, avgMessagesPerUser: overviewAnalytics.avgMessagesPerUser, responseRate: overviewAnalytics.responseRate, medianResponseTime: overviewAnalytics.medianResponseTime, medianModeratorResponseTime: overviewAnalytics.medianModeratorResponseTime, moderatorResponseCount: overviewAnalytics.moderatorResponseCount, effectiveResponseTime: overviewAnalytics.effectiveResponseTime, effectiveResponseCount: overviewAnalytics.effectiveResponseCount, effectiveHumanCount: overviewAnalytics.effectiveHumanCount, effectiveBotCount: overviewAnalytics.effectiveBotCount, thumbsUp: overviewAnalytics.thumbsUp, thumbsDown: overviewAnalytics.thumbsDown, thumbsUpRate: overviewAnalytics.thumbsUpRate, correctedResponses: overviewAnalytics.correctedResponses }, dailyTrends, flagBreakdown, summaries: { overview: aiSummary, topTopics, topicsSummary, keyInsights: aiInsights, recommendations: aiRecommendations, aiGenerated: aiSummary.length > 200 } }
            const pdfBlob = await generateTelerivetReport(reportData)
            const filename = `analytics-report-${date_from}-to-${date_to}.pdf`
            downloadPDF(pdfBlob, filename)
            return { success: true, filename, date_range: `${format(reportFrom, "MMM d, yyyy")} - ${format(reportTo, "MMM d, yyyy")}`, ai_generated: aiSummary.length > 200, message: `PDF "${filename}" generated and downloaded.` }
          } catch (err) { return { success: false, error: `Failed: ${(err as Error).message}` } }
        }
      }),
    }
  }, [contacts, messages, contactMap, commentsList, correctedResponsesList, overviewAnalytics, dailyTrends, flagBreakdown, dateRangeLabel, projects, allProjects, teamId, allVarKeys, pinnedVarKeys, pinnedVarConfigs, onTogglePin, onUpdatePinnedConfig, formFieldMeta])

  return { createTools }
}

function formatDuration(seconds: number): string {
  if (seconds === 0) return "N/A"
  if (seconds < 60) return `${Math.round(seconds)}s`
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`
  return `${(seconds / 3600).toFixed(1)}h`
}
